import jexl from 'jexl-extended'
import { dateTimeToMillis, formatBase, millis, toNumber } from 'jexl-extended/extended-grammar'
import type Expression from 'jexl/Expression'
import { create, evaluateDependencies, toDependencies } from 'mathjs'
import { interpolateColors } from './interpolate-colors'
import {
  getDisplayUnitByProperty,
  getDisplayName,
  getDisplayNames,
  getDescription,
  formatNumber
} from 'src/utils/dtdl'
import { isString } from 'src/utils/type-checks'
import { useModelsStore, useAdminModelsStore } from 'src/stores/models'
import { useParametersStore } from 'src/stores/parameters'
import type { DtdlArraySchema, DtdlComplexSchema, DtdlEnumSchema } from 'src/models/Dtdl'
import { useDigitalTwinsStore } from 'src/stores/digital-twins'
import { getColorByExpression, getContrastColor, getPaletteColor } from 'src/utils/colors'
import { currentLanguage } from 'src/utils/language'
import { msalInstance } from 'src/services/auth'
import type { CommentKeys } from './comments'
import { getCommentValue, getCommentValueString } from './comments'
import { removeAllSpecialChars } from './validate'
import 'src/utils/jexl-extensions' // lazy use-case helpers (docs/developer/config-extensions.md)

export interface Context {
  [key: string]: unknown
}

const math = create({ evaluateDependencies, toDependencies })

// DTDL Guidelines
// Mustaches should only be used for strings
// We want to remove it from jexlEval but it stays in jexlEvalString
// - [jexlEval] property names with the suffix Expression
//      - Never start with '=' (because it's always an expression)
//      - They should not contain mustache expressions
// - [jexlEvalString] property names without the suffix Expression (mostly strings)
//      - Should start with '=' if it's an expression
//      - Or they contain mustache expressions that return a string

// add color interpolation function for use in expressions
// usage: '= interpolateColors(co2, 400, "#0da642", 800, "#f8da40", 1200, "#e41f13")'
jexl.addFunction('interpolateColors', interpolateColors)

// Add date comparison helpers for validation
jexl.addFunction('isAfter', (dateA: string | undefined, dateB: string | undefined): boolean => {
  if (!dateA || !dateB) return true // If either date is null/undefined, validation passes
  try {
    return new Date(dateA) > new Date(dateB)
  } catch {
    return false
  }
})

jexl.addFunction(
  'isAfterOrEqual',
  (dateA: string | undefined, dateB: string | undefined): boolean => {
    if (!dateA || !dateB) return true // If either date is null/undefined, validation passes
    try {
      return new Date(dateA) >= new Date(dateB)
    } catch {
      return false
    }
  }
)

jexl.addFunction('isBefore', (dateA: string | undefined, dateB: string | undefined): boolean => {
  if (!dateA || !dateB) return true // If either date is null/undefined, validation passes
  try {
    return new Date(dateA) < new Date(dateB)
  } catch {
    return false
  }
})

jexl.addFunction(
  'isBeforeOrEqual',
  (dateA: string | undefined, dateB: string | undefined): boolean => {
    if (!dateA || !dateB) return true // If either date is null/undefined, validation passes
    try {
      return new Date(dateA) <= new Date(dateB)
    } catch {
      return false
    }
  }
)

// type conversions
jexl.addTransform('toDouble', toNumber)
jexl.addTransform('toFloat', toNumber)
jexl.addTransform('hexToFloat32', (hex: string): number => {
  // Expects IEEE 754 floating point format
  // Create a buffer with 4 bytes and create a DataView
  const buffer = new ArrayBuffer(4)
  const view = new DataView(buffer)
  // Set the 32-bit signed integer value
  view.setInt32(0, Number.parseInt(hex, 16), false)
  // Get the float value from the DataView
  return view.getFloat32(0, false)
})
jexl.addTransform('toHex', (val: unknown) => formatBase(val, 16))
jexl.addTransform('toEpoch', dateTimeToMillis)
jexl.addTransform('excelTicksToMillis', (n: number): number => (n - 25569) * 86400000)
jexl.addTransform(
  'lookup',
  (searchVal: unknown, options: Array<[key: unknown, value: unknown]>) =>
    options?.find((o) => o?.[0] === searchVal)?.[1]
)
jexl.addTransform('convertUnit', (val: string | number, fromUnit: string, toUnit: string) => {
  try {
    return math.evaluate(`${val} ${fromUnit} to ${toUnit}`).toNumber()
  } catch {
    return undefined
  }
})
jexl.addTransform('regexpTest', (val: unknown, regexp: unknown): boolean | undefined => {
  const stringToRegex = (s: string) => {
    const m = /^([/~@;%#'])(.*?)\1([gimsuy]*)$/.exec(s)
    return m ? new RegExp(m[2], m[3]) : new RegExp(s)
  }
  if (!regexp || typeof regexp !== 'string' || (typeof regexp === 'string' && regexp?.length <= 2))
    return undefined
  const _regexp = stringToRegex(regexp)
  const _val = typeof val === 'object' ? JSON.stringify(val) : `${val}`
  try {
    return _regexp.test(_val)
  } catch {
    return undefined
  }
})

type ModelsStore = ReturnType<typeof useModelsStore>

/**
 * Registers a JEXL transform (and optionally a function) for both the regular
 * 'models' store and the 'adminModels' store. Both variants share the same
 * implementation and only differ by which store they read from.
 */
const addModelTransform = <T extends (...args: never[]) => unknown>(
  name: string,
  build: (getStore: () => ModelsStore) => T,
  alsoFunction = false
): void => {
  const register = (suffix: string, getStore: () => ModelsStore) => {
    const fn = build(getStore) as unknown as Parameters<typeof jexl.addTransform>[1]
    jexl.addTransform(`${name}${suffix}`, fn)
    if (alsoFunction) jexl.addFunction(`${name}${suffix}`, fn)
  }
  register('', () => useModelsStore())
  register('Admin', () => useAdminModelsStore())
}

addModelTransform(
  'formatPropertyValue',
  (getStore) =>
    (value: unknown, model: string, prop: string): string | undefined => {
      if (value === undefined) return undefined
      const property = getStore().propertyByName(model, prop)
      if (!property) return `${value}`
      if ((property.schema as DtdlComplexSchema)['@type'] === 'Enum') {
        const _schema = property.schema as DtdlEnumSchema
        const _enumValue = _schema.enumValues.find((e) => e.enumValue === value)
        if (!_enumValue) return `${value}`
        return getDisplayName(_enumValue)
      }
      return `${value} ${getDisplayUnitByProperty(property)}`
    }
)

addModelTransform(
  'propertyDisplayName',
  (getStore) =>
    (model: string, prop: string, lang?: string): string | undefined =>
      getDisplayName(getStore().propertyByName(model, prop), lang),
  true
)

addModelTransform(
  'propertyDisplayNames',
  (getStore) =>
    (model: string, prop: string, lang?: string): string[] =>
      getDisplayNames(getStore().propertyByName(model, prop), lang),
  true
)

addModelTransform(
  'displayNameProperty',
  (getStore) =>
    (model: string, displayName: string, lang?: string): string | undefined =>
      getStore().propertyByDisplayName(model, displayName, lang)?.name,
  true
)

addModelTransform(
  'propertyDescription',
  (getStore) =>
    (model: string, prop: string, lang?: string): string | undefined =>
      getDescription(getStore().propertyByName(model, prop), lang)
)

const getEnumValues = (
  store: ModelsStore,
  model: string,
  prop: string
): DtdlEnumSchema['enumValues'] | undefined => {
  const property = store.propertyByName(model, prop)
  if (!property?.schema) return undefined
  const schema = property.schema as DtdlComplexSchema
  if (schema['@type'] === 'Enum') return (schema as DtdlEnumSchema).enumValues
  // also unwrap Array<Enum> (e.g. NexusEnvironment.scenarios)
  if (schema['@type'] === 'Array') {
    const elementSchema = (schema as DtdlArraySchema).elementSchema as DtdlComplexSchema
    if (elementSchema?.['@type'] === 'Enum') return (elementSchema as DtdlEnumSchema).enumValues
  }
  return undefined
}

addModelTransform(
  'propertyEnumValues',
  (getStore) => (model: string, prop: string) => getEnumValues(getStore(), model, prop)
)

addModelTransform(
  'propertyEnumValueDisplayName',
  (getStore) =>
    (model: string, prop: string, enumValue: unknown, lang?: string): string | undefined => {
      const enumValueDef = getEnumValues(getStore(), model, prop)?.find(
        (e) => `${e.enumValue}` === `${enumValue}`
      )
      return getDisplayName(enumValueDef, lang)
    },
  true
)

addModelTransform(
  'propertyEnumValueColor',
  (getStore) =>
    (model: string, prop: string, enumValue: unknown): string | undefined => {
      const enumValueDef = getEnumValues(getStore(), model, prop)?.find(
        (e) => `${e.enumValue}` === `${enumValue}`
      )
      if (!enumValueDef?.comment) return undefined
      const colorExpr = getCommentValueString(enumValueDef.comment, 'color')
      return colorExpr ? getPaletteColor(colorExpr) : undefined
    },
  true
)

addModelTransform(
  'propertyUnit',
  (getStore) =>
    (model: string, prop: string): string | undefined => {
      const property = getStore().propertyByName(model, prop)
      if (!property) return undefined
      return getDisplayUnitByProperty(property)
    }
)

addModelTransform(
  'modelDisplayName',
  (getStore) =>
    (model: string, lang?: string): string | undefined =>
      getStore().modelDisplayName(model, lang),
  true
)

addModelTransform(
  'modelDescription',
  (getStore) =>
    (model: string, lang?: string): string | undefined =>
      getStore().modelDescription(model, lang),
  true
)

addModelTransform(
  'modelComment',
  (getStore) =>
    (model: string, key: CommentKeys): string | boolean | undefined => {
      const rawCommentData = getStore().modelComment(model)
      if (!rawCommentData || !key) return undefined
      return getCommentValue(rawCommentData, key)
    },
  true
)

// will return the modelIds for the given baseIds, if the baseId is not found it will return an empty array
// will return child modelIds for the given baseIds, if the baseId is not found it will return an empty array
addModelTransform(
  'modelIdsByBaseIds',
  (getStore) =>
    (baseIds: string | string[]): string[] =>
      getStore().idsByBaseIds(baseIds),
  true
)

// will return the baseIds for the given modelIds, if the modelId is not found it will return an empty array
// will return parent modelIds for the given modelIds, if the modelId is not found it will return an empty array
addModelTransform(
  'baseIdsByModelIds',
  (getStore) =>
    (modelIds: string | string[]): string[] => {
      const _modelIds = Array.isArray(modelIds) ? modelIds : [modelIds]
      return [...new Set(_modelIds.flatMap((id) => getStore().bases(id)))]
    },
  true
)

jexl.addFunction('twin', (twinId: string) => useDigitalTwinsStore().twins[twinId])
jexl.addTransform('twin', (twinId: string) => useDigitalTwinsStore().twins[twinId])

jexl.addFunction('twinRels', (twinId: string) => useDigitalTwinsStore().relationships[twinId])
jexl.addTransform('twinRels', (twinId: string) => useDigitalTwinsStore().relationships[twinId])

const currentAccount = () => {
  return msalInstance.getActiveAccount()
}
jexl.addFunction('currentAccount', () => currentAccount())

jexl.addTransform('currentLanguage', () => currentLanguage.value)
jexl.addFunction('currentLanguage', () => currentLanguage.value)

jexl.addTransform('localeString', (value: number, digits?: number) => formatNumber(value, digits))
jexl.addTransform('dateToLocaleString', (value: string) =>
  new Date(value).toLocaleString(navigator.languages[0])
)
jexl.addTransform('dateToRelativeTime', (value: string) => {
  const getMonth = (month: number) => {
    switch (month) {
      case 1:
        return 'January'
      case 2:
        return 'February'
      case 3:
        return 'March'
      case 4:
        return 'April'
      case 5:
        return 'May'
      case 6:
        return 'June'
      case 7:
        return 'July'
      case 8:
        return 'August'
      case 9:
        return 'September'
      case 10:
        return 'October'
      case 11:
        return 'November'
      case 12:
        return 'December'
      default:
        return 'December'
    }
  }
  if (value == null || value === '' || value === undefined) return ''
  const _date = new Date(value)
  const _delta = Date.now() - _date.getTime()
  const _minute = 60 * 1000
  const _hour = _minute * 60
  const _day = _hour * 24
  const _week = _day * 7
  // Past dates
  if (_delta >= 0) {
    if (_delta < _minute) {
      const seconds = Math.floor(_delta / 1000)
      return seconds <= 1 ? 'just now' : `${seconds} seconds ago`
    }
    if (_delta < _hour) {
      const minutes = Math.floor(_delta / _minute)
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
    }
    if (_delta < _day) {
      const hours = Math.floor(_delta / _hour)
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }
    if (_delta < _week) {
      const days = Math.floor(_delta / _day)
      return days === 1 ? '1 day ago' : `${days} days ago`
    }
  }
  // Future dates
  if (_delta < 0) {
    const _absdelta = Math.abs(_delta)
    if (_absdelta < _minute) {
      const seconds = Math.floor(_absdelta / 1000)
      return seconds <= 1 ? 'just now' : `${seconds} from now`
    }
    if (_absdelta < _hour) {
      const minutes = Math.floor(_absdelta / _minute)
      return minutes === 1 ? '1 minute from now' : `${minutes} minutes from now`
    }
    if (_absdelta < _day) {
      const hours = Math.floor(_absdelta / _hour)
      return hours === 1 ? '1 hour from now' : `${hours} hours from now`
    }
    if (_absdelta < _week) {
      const days = Math.floor(_absdelta / _day)
      return days === 1 ? '1 day from now' : `${days} days from now`
    }
  }
  return `${getMonth(_date.getMonth() + 1)} ${_date.getDate()} ${_date.getFullYear()}` // 10 March 2021;
})

jexl.addTransform('formatLargeNumbers', (value: number, digits?: number): string => {
  let _value: string
  const _valueNumber: number = toNumber(value)
  if (Number.isNaN(_valueNumber)) return String(value)
  if (_valueNumber >= 1e9) {
    _value = `${(_valueNumber / 1e9).toLocaleString(navigator.languages[0], { maximumFractionDigits: digits || 2 })}B` // billions
  } else if (_valueNumber >= 1e6) {
    _value = `${(_valueNumber / 1e6).toLocaleString(navigator.languages[0], { maximumFractionDigits: digits || 2 })}M` // millions
  } else if (_valueNumber >= 1e3) {
    _value = `${(_valueNumber / 1e3).toLocaleString(navigator.languages[0], { maximumFractionDigits: digits || 2 })}K` // thousands
  } else {
    _value = _valueNumber.toLocaleString(navigator.languages[0], {
      maximumFractionDigits: digits || 2
    })
  }
  return _value
})

/**
 * Transform to format a number with a specified precision
 * Usage:
 *   - '=toPrecision(123.456, 2)' => '1.2e+2'
 */
jexl.addTransform('toPrecision', (value: number, digits?: number): string => {
  const _valueNumber: number = toNumber(value)
  if (Number.isNaN(_valueNumber)) return String(value)
  return _valueNumber.toPrecision(digits)
})

/**
 * Transform to get parameter labels from parameter values
 * Usage:
 *   - '=parameterLabel(selectedParameter(s), "parameterName")'
 *   - '=parameterLabel(selectedParameter(s), "parameterName", "propName")'
 */
jexl.addTransform('parameterLabel', (value: unknown, parameter: string, prop?: string) => {
  const { parameterConfigsMap, parameterOptionsMap } = useParametersStore()
  const parameterConfig = Object.entries(parameterConfigsMap).find(
    ([, config]) => `${config.variableName}` === `${parameter}`
  )
  if (!parameterConfig) return value
  const parameterOptions = parameterOptionsMap[parameterConfig[0]]
  if (!parameterOptions) return value
  if (parameterConfig[1].multiple) {
    const _value = JSON.parse(`${value}`)
    if (!Array.isArray(_value)) return value
    return _value.map((v) => {
      const parameterOption = parameterOptions?.find((o) => `${o.value}` === `${v}`)
      return parameterOption?.displayText || (prop && parameterOption?.[prop]) || v
    })
  } else {
    const parameterOption = parameterOptions?.find((o) => `${o.value}` === `${value}`)
    return parameterOption?.displayText || (prop && parameterOption?.[prop]) || value
  }
})

/**
 * Transform to get all parameter options for a given parameter
 * Usage:
 *   - '=parameterOptions("parameterName")'
 */
jexl.addTransform('parameterOptions', (parameter: string) => {
  const { parameterConfigsMap, parameterOptionsMap } = useParametersStore()
  const parameterConfig = Object.entries(parameterConfigsMap).find(
    ([, config]) => `${config.variableName}` === `${parameter}`
  )
  if (!parameterConfig) return []
  return parameterOptionsMap[parameterConfig[0]] || []
})

/**
 * Transform to get the display name for a given parameter
 * Usage:
 *   - '=parameterDisplayName("parameterName")'
 */
jexl.addTransform('parameterDisplayName', (parameter: string) => {
  const { parameterConfigsMap } = useParametersStore()
  const parameterConfig = Object.entries(parameterConfigsMap).find(
    ([, config]) => `${config.variableName}` === `${parameter}`
  )
  if (!parameterConfig) return undefined
  return parameterConfig[1].displayName
})

// eslint-disable-next-line no-secrets/no-secrets
/**
 * Transform to remove all special characters from a string and replace them with an optional replacement character (default is '_')
 * Usage:
 *   - '=removeAllSpecialChars("string with special chars!@#$%^&*()")' => 'string_with_special_chars'
 *   - '=removeAllSpecialChars("string with special chars!@#$%^&*()", "-")' => 'string-with-special-chars'
 */
jexl.addTransform('removeAllSpecialChars', (value: string, replacement: string = '_') => {
  if (typeof value !== 'string') return value
  if (value == null || value === '' || value === undefined) return ''
  return value.replace(removeAllSpecialChars, replacement)
})

jexl.addTransform('getColorByExpression', getColorByExpression)
jexl.addFunction('getColorByExpression', getColorByExpression)

jexl.addTransform('getPaletteColor', getPaletteColor)
jexl.addFunction('getPaletteColor', getPaletteColor)

/**
 * Transform to get a color with enough contrast based on the given color
 * Usage:
 *   - '=getContrastColor(itemColor)' => '#000000' or '#ffffff'
 *   - '=getContrastColor(itemColor, "white", "black")'
 */
jexl.addTransform('getContrastColor', getContrastColor)
jexl.addFunction('getContrastColor', getContrastColor)

const rgba = (hex: string, a: number): string => {
  const n = Number.parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}
jexl.addTransform('hexToRgba', rgba)
jexl.addFunction('hexToRgba', rgba)

// Functions
jexl.addFunction('currentEpoch', millis)
jexl.addFunction(
  'lookup',
  (searchVal: unknown, options: Array<[key: unknown, value: unknown]>) =>
    options?.find((o) => o?.[0] === searchVal)?.[1]
)
jexl.addFunction(
  'bubbleSize',
  (value: number, valueMin: number, valueMax: number, radiusMin: number, radiusMax: number) =>
    radiusMin +
    ((Math.sqrt(value) - Math.sqrt(valueMin)) / (Math.sqrt(valueMax) - Math.sqrt(valueMin))) *
      (radiusMax - radiusMin)
)

/** Holds precompiled JEXL expressions to improve performance */
const expressionCache: { [key: string]: Expression } = {}

type EvaluationResult<T> = { success: true; value: T } | { success: false }

/**
 * Compiles (with cache) and evaluates an expression against the context.
 * Never throws: an expression that cannot be compiled or evaluated is reported as a failure so
 * callers can decide what to render instead of leaking the raw expression source to the UI.
 */
const tryEvaluate = <T>(expression: string, context?: Context): EvaluationResult<T> => {
  try {
    const _expr = expressionCache[expression] ?? jexl.createExpression(expression)
    expressionCache[expression] = _expr
    return { success: true, value: _expr.evalSync(context) as T }
  } catch (e) {
    console.error(e)
    return { success: false }
  }
}

/**
 * Interpolates a single mustache token. An expression that cannot be evaluated (unknown transform,
 * invalid subscript on an empty value, ...) renders as an empty string instead of showing the
 * expression source.
 */
const evalMustache = (expression: string, context?: Context): string => {
  const _result = tryEvaluate<string>(expression, context)
  return _result.success ? `${_result.value}` : ''
}

/**
 * Evaluates the supplied expression and returns a string
 * @param expression Expression to be evaluated or to be returned
 * @param context Context object (usually the digital twin itself)
 */
export const jexlEvalString = (expression?: string | null, context?: Context) => {
  // if there is no value and the value is not a String return
  if (expression == null) return

  if (expression.startsWith('=')) {
    return jexlEval<string>(expression, context)
  } else {
    const _mustacheMatches = expression.match(MustacheRegex)
    if (_mustacheMatches) {
      let _value = expression
      for (const match of _mustacheMatches) {
        const _expression = match.replaceAll('{{', '').replaceAll('}}', '').trim()
        const _replacement = evalMustache(_expression, context)
        _value = `${_value}`.replace(match, _replacement)
      }
      return _value
    }
  }
  return expression
}

/**
 * Evaluates the supplied expression
 * @param expression Expression to be evaluated
 * @param context Context object (usually the digital twin itself)
 */
export const jexlEval = <T>(expression?: string | null, context?: Context) => {
  // if there is no value and the value is not a String return
  if (expression == null) return
  // if the expression is not a string , return the value as is
  if (!isString(expression)) return expression as unknown as T

  let _value = expression.startsWith('=') ? expression.substring(1) : expression
  // if the expression is a mustache template, replace the mustache values with the context values
  // TODO: Should be removed from jexlEval in the future, see guidelines on top of this file

  const _mustacheMatches = _value.match(MustacheRegex)
  if (_mustacheMatches) {
    for (const match of _mustacheMatches) {
      const _expression = match.replaceAll('{{', '').replaceAll('}}', '').trim()
      const _replacement = evalMustache(_expression, context)
      _value = `${_value}`.replace(match, _replacement)
    }
  }

  const _result = tryEvaluate<T>(_value, context)

  // If invalid expression return the value as is
  return _result.success ? _result.value : (_value as unknown as T)
}

/**
 * Try to evaluate the supplied expression
 * @param expressionOrValue Value to return or Expression to be evaluated
 * @param context Context object (usually the digital twin itself)
 */
export const tryJexlEval = <T>(
  expressionOrValue?: string | null,
  context?: Context
  // eslint-disable-next-line sonarjs/function-return-type
): string | T | undefined => {
  // if there is no value and the value is not a String return
  if (!expressionOrValue || !isString(expressionOrValue) || expressionOrValue === null) return
  if (expressionOrValue?.startsWith('=')) return jexlEval<T>(expressionOrValue, context)
  if (!isValidExpression(expressionOrValue, context)) return expressionOrValue
  const _eval = jexlEval<T>(expressionOrValue, context)
  if (
    // TODO maybe more checks necessary
    _eval === undefined ||
    (typeof _eval === 'number' && Number.isNaN(_eval))
  ) {
    return expressionOrValue
  }
  return _eval
}

/**
 * Evaluates the supplied expression and check if it's valid
 * @param expression Expression to be evaluated
 * @param context Context object (usually the digital twin itself)
 */
export const isValidExpression = (expression?: string | null, context?: Context): boolean => {
  // if there is no value and the value is not a String return
  if (expression == null) return false
  // if the expression is not a string , return the value as is
  if (!isString(expression)) return false

  const _value = expression.startsWith('=') ? expression.substring(1) : expression
  try {
    const _expr = expressionCache[_value] ?? jexl.createExpression(_value)
    expressionCache[_value] = _expr
    _expr.evalSync(context)
    return true
  } catch {
    return false
  }
}

/**
 * removing invalid marks for jexl, remove spaces and all special chars
 * @param {string} element
 * @returns {string} parsed element without spaces and all special chars
 */
export const transformForJexl = (element: string): string => {
  // remove spaces

  return element.replace(/\s+/g, '').replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '')
}

/**
 * Validates a JEXL expression against a context
 * @param expression The JEXL expression to validate
 * @param context The context object (usually the digital twin)
 * @returns Object with isValid boolean and optional error message
 */
export const validateJexlExpression = (
  expression: string | undefined,
  context?: Context
): { isValid: boolean; error?: string } => {
  if (!expression || !isString(expression)) {
    return { isValid: true } // No validation rule means valid
  }

  try {
    const result = jexlEval(expression, context)
    if (typeof result !== 'boolean') {
      return {
        isValid: false,
        error: 'Validation expression must return a boolean value'
      }
    }
    return {
      isValid: result,
      error: result ? undefined : 'Validation failed'
    }
  } catch (error) {
    return {
      isValid: false,
      error: `Validation expression error: ${error instanceof Error ? error.message : 'Unknown error'}`
    }
  }
}

// better option for removing special chars in twinIds

export const MustacheRegex = /\{\{([^{}]*)\}\}/g
// export const MustacheRegex = /{{(?:\s*[^\s{}]+\s*|{\s*[^\s{}]+\s*}|[&#^/>!]\s*[^\s{}]+\s*)}}/g
